/**
 * reducers/step-factories.ts
 * desc: client step factories
 */
import type { Reducer } from '@reduxjs/toolkit';
import { sortAz } from '../../utilities';
import type { StepFactoriesAction, StepFactoryVersionRef } from '../actions/step-factories';
import type { StepFactoryDto, StepFactoryVersionAutocompleteSuggestions, StepFactoryVersionDto } from '../../types/api/step-factories';
import type { ComponentInfo } from '../../types/api/components';
import type { ApiErrorJson } from '../../utilities/error';

// the last autocomplete lookup: its suggestions or the API error, stamped with an id per fetch
export interface StepFactoryVersionAutocomplete {
    id?: string;
    details: StepFactoryVersionAutocompleteSuggestions | Record<string, never>;
    error: ApiErrorJson | Record<string, never>;
}

export interface StepFactoriesState {
    isLoading: {
        createStepFactory: boolean;
        createStepFactoryVersion: boolean;
        deleteStepFactory: boolean;
        deleteStepFactoryVersion: boolean;
        getStepFactory: boolean;
        getStepFactoryUsage: boolean;
        getStepFactoryVersion: boolean;
        getStepFactoryVersionAutocomplete: boolean;
        getStepFactoryVersionUsage: boolean;
        getStepFactoryVersions: boolean;
        getStepFactories: boolean;
        updateStepFactory: boolean;
        updateStepFactoryVersion: boolean;
    };
    // empty until a factory arrives; the version delete trims its versionIds in place
    stepFactoryDetail: Partial<StepFactoryDto>;
    stepFactoryList: StepFactoryDto[];
    stepFactoryVersionAutocomplete: StepFactoryVersionAutocomplete;
    // an empty object until a version arrives
    stepFactoryVersionDetail: StepFactoryVersionDto | Record<string, never>;
    // the versions of the factory a page shows, oldest first
    stepFactoryVersionList: StepFactoryVersionDto[];
    stepFactoryUsage: ComponentInfo[];
    stepFactoryVersionUsage: ComponentInfo[];
}

const INITIAL_STATE: StepFactoriesState = {
    isLoading: {
        createStepFactory: false,
        createStepFactoryVersion: false,
        deleteStepFactory: false,
        deleteStepFactoryVersion: false,
        getStepFactory: false,
        getStepFactoryUsage: false,
        getStepFactoryVersion: false,
        getStepFactoryVersionAutocomplete: false,
        getStepFactoryVersionUsage: false,
        getStepFactoryVersions: false,
        getStepFactories: false,
        updateStepFactory: false,
        updateStepFactoryVersion: false,
    },
    stepFactoryDetail: {},
    stepFactoryList: [],
    stepFactoryVersionAutocomplete: {
        details: {},
        error: {}
    },
    stepFactoryVersionDetail: {},
    stepFactoryVersionList: [],
    stepFactoryUsage: [],
    stepFactoryVersionUsage: []
};


function stepFactoriesReducer(state: StepFactoriesState = INITIAL_STATE, action: StepFactoriesAction): StepFactoriesState {
    switch (action.type) {
        // Clear Step Factories
        case 'CLEAR_STEP_FACTORIES':
            return _clearStepFactories();

        // CREATE Step Factory (Instance)
        case 'CREATE_STEP_FACTORY_ERROR':
            return _createStepFactoryError(state);
        case 'CREATE_STEP_FACTORY_REQUEST':
            return _createStepFactoryRequest(state);
        case 'CREATE_STEP_FACTORY_SUCCESS':
            return _createStepFactorySuccess(state, action.payload);

        // CREATE Step Factory Version (Instance)
        case 'CREATE_STEP_FACTORY_VERSION_ERROR':
            return _createStepFactoryVersionError(state);
        case 'CREATE_STEP_FACTORY_VERSION_REQUEST':
            return _createStepFactoryVersionRequest(state);
        case 'CREATE_STEP_FACTORY_VERSION_SUCCESS':
            return _createStepFactoryVersionSuccess(state, action.payload);

        // DELETE Step Factory (Instance)
        case 'DELETE_STEP_FACTORY_ERROR':
            return _deleteStepFactoryError(state);
        case 'DELETE_STEP_FACTORY_REQUEST':
            return _deleteStepFactoryRequest(state);
        case 'DELETE_STEP_FACTORY_SUCCESS':
            return _deleteStepFactorySuccess(state, action.payload);

        // DELETE Step Factory Version (Instance)
        case 'DELETE_STEP_FACTORY_VERSION_ERROR':
            return _deleteStepFactoryVersionError(state);
        case 'DELETE_STEP_FACTORY_VERSION_REQUEST':
            return _deleteStepFactoryVersionRequest(state);
        case 'DELETE_STEP_FACTORY_VERSION_SUCCESS':
            return _deleteStepFactoryVersionSuccess(state, action.payload);

        // GET Step Factory (Instance)
        case 'GET_STEP_FACTORY_ERROR':
            return _getStepFactoryError(state);
        case 'GET_STEP_FACTORY_REQUEST':
            return _getStepFactoryRequest(state);
        case 'GET_STEP_FACTORY_SUCCESS':
            return _getStepFactorySuccess(state, action.payload);

        // GET Step Factory (Instance)
        case 'GET_STEP_FACTORY_USAGE_ERROR':
            return _getStepFactoryUsageError(state);
        case 'GET_STEP_FACTORY_USAGE_REQUEST':
            return _getStepFactoryUsageRequest(state);
        case 'GET_STEP_FACTORY_USAGE_SUCCESS':
            return _getStepFactoryUsageSuccess(state, action.payload);

        // GET Step Factory Version (Instance)
        case 'GET_STEP_FACTORY_VERSION_ERROR':
            return _getStepFactoryVersionError(state);
        case 'GET_STEP_FACTORY_VERSION_REQUEST':
            return _getStepFactoryVersionRequest(state);
        case 'GET_STEP_FACTORY_VERSION_SUCCESS':
            return _getStepFactoryVersionSuccess(state, action.payload);

        // GET Step Factory Version (Instance)
        case 'GET_STEP_FACTORY_VERSION_AUTOCOMPLETE_ERROR':
            return _getStepFactoryVersionAutocompleteError(state, action.payload);
        case 'GET_STEP_FACTORY_VERSION_AUTOCOMPLETE_REQUEST':
            return _getStepFactoryVersionAutocompleteRequest(state);
        case 'GET_STEP_FACTORY_VERSION_AUTOCOMPLETE_SUCCESS':
            return _getStepFactoryVersionAutocompleteSuccess(state, action.payload);

        // GET Step Factory Version (Instance)
        case 'GET_STEP_FACTORY_VERSIONS_ERROR':
            return _getStepFactoryVersionsError(state);
        case 'GET_STEP_FACTORY_VERSIONS_REQUEST':
            return _getStepFactoryVersionsRequest(state);
        case 'GET_STEP_FACTORY_VERSIONS_SUCCESS':
            return _getStepFactoryVersionsSuccess(state, action.payload);
        case 'GET_STEP_FACTORY_VERSION_USAGE_ERROR':
            return _getStepFactoryVersionUsageError(state);
        case 'GET_STEP_FACTORY_VERSION_USAGE_REQUEST':
            return _getStepFactoryVersionUsageRequest(state);
        case 'GET_STEP_FACTORY_VERSION_USAGE_SUCCESS':
            return _getStepFactoryVersionUsageSuccess(state, action.payload);

        // GET Step Factories (Collection)
        case 'GET_STEP_FACTORIES_ERROR':
            return getStepFactoriesError(state);
        case 'GET_STEP_FACTORIES_REQUEST':
            return getStepFactoriesRequest(state);
        case 'GET_STEP_FACTORIES_SUCCESS':
            return getStepFactoriesSuccess(state, action.payload);

        // Update Step Factories (Instance)
        case 'UPDATE_STEP_FACTORY_ERROR':
            return _updateStepFactoryError(state);
        case 'UPDATE_STEP_FACTORY_REQUEST':
            return _updateStepFactoryRequest(state);
        case 'UPDATE_STEP_FACTORY_SUCCESS':
            return _updateStepFactorySuccess(state, action.payload);

        // Update Step Factories Version (Instance)
        case 'UPDATE_STEP_FACTORY_VERSION_ERROR':
            return _updateStepFactoryVersionError(state);
        case 'UPDATE_STEP_FACTORY_VERSION_REQUEST':
            return _updateStepFactoryVersionRequest(state);
        case 'UPDATE_STEP_FACTORY_VERSION_SUCCESS':
            return _updateStepFactoryVersionSuccess(state, action.payload);

        default:
            return state;
    }
}



//*************************************************
//*****   Clear Step Factories   ******************
//*************************************************
function _clearStepFactories(): StepFactoriesState {
    return INITIAL_STATE;
}



//*************************************************
//*****   CREATE Step Factory (Instance)   ********
//*************************************************
function _createStepFactoryError(state: StepFactoriesState): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createStepFactory: false,
        },
    };
}

function _createStepFactoryRequest(state: StepFactoriesState): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createStepFactory: true,
        },
    };
}

function _createStepFactorySuccess(state: StepFactoriesState, payload: StepFactoryDto): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createStepFactory: false,
        },
        stepFactoryList: [ ...state.stepFactoryList, payload ],
    };
}



//*************************************************
//***   CREATE Step Factory Version (Instance)   **
//*************************************************
function _createStepFactoryVersionError(state: StepFactoriesState): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createStepFactoryVersion: false,
        },
    };
}

function _createStepFactoryVersionRequest(state: StepFactoriesState): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createStepFactoryVersion: true,
        },
    };
}

function _createStepFactoryVersionSuccess(state: StepFactoriesState, payload: StepFactoryVersionDto): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createStepFactoryVersion: false,
        },
        stepFactoryVersionDetail: payload,
    };
}



//*************************************************
//*****   DELETE Step Factory (Instance)   ********
//*************************************************
function _deleteStepFactoryError(state: StepFactoriesState): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteStepFactory: false,
        },
    };
}

function _deleteStepFactoryRequest(state: StepFactoriesState): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteStepFactory: true,
        },
    };
}

function _deleteStepFactorySuccess(state: StepFactoriesState, stepFactoryId: string): StepFactoriesState {
    // create new list without deleted step factory
    const [ ...stepFactoriesList ] = state.stepFactoryList;
    const deletedIndex = state.stepFactoryList.findIndex(x => x.id === stepFactoryId);
    if (deletedIndex > -1) stepFactoriesList.splice(deletedIndex, 1);

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteStepFactory: false,
        },
        stepFactoryList: stepFactoriesList,
    };
}



//*************************************************
//***   DELETE Step Factory Version (Instance)   **
//*************************************************
function _deleteStepFactoryVersionError(state: StepFactoriesState): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteStepFactoryVersion: false,
        },
    };
}

function _deleteStepFactoryVersionRequest(state: StepFactoriesState): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteStepFactoryVersion: true,
        },
    };
}

function _deleteStepFactoryVersionSuccess(state: StepFactoriesState, payload: StepFactoryVersionRef): StepFactoriesState {
    const { stepFactoryId, stepFactoryVersionId } = payload;

    // create new list without deleted step factory
    const stepFactoriesList = structuredClone(state.stepFactoryList);
    const deletedIndex = state.stepFactoryList.findIndex(x => x.id === stepFactoryId);
    if (deletedIndex > -1) {
        stepFactoriesList[deletedIndex].versionIds = stepFactoriesList[deletedIndex].versionIds?.filter(x => x !== stepFactoryVersionId);
    }

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteStepFactoryVersion: false,
        },
        stepFactoryVersionList: state.stepFactoryVersionList.filter(item => item.versionId !== stepFactoryVersionId),
        stepFactoryDetail: {
            ...state.stepFactoryDetail,
            versionIds: state.stepFactoryDetail?.versionIds?.filter(item => item !== stepFactoryVersionId)
        },
        stepFactoryList: stepFactoriesList,
    };
}



//*************************************************
//*****   GET Step Factory (Instance)   ***********
//*************************************************
function _getStepFactoryError(state: StepFactoriesState): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStepFactory: false,
        },
    };
}

function _getStepFactoryRequest(state: StepFactoriesState): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStepFactory: true,
        },
    };
}

function _getStepFactorySuccess(state: StepFactoriesState, payload: StepFactoryDto): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStepFactory: false,
        },
        stepFactoryDetail: payload,
    };
}



//*************************************************
//*****   GET Step Factory Usage   ****************
//*************************************************
function _getStepFactoryUsageError(state: StepFactoriesState): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStepFactoryUsage: false,
        },
    };
}

function _getStepFactoryUsageRequest(state: StepFactoriesState): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStepFactoryUsage: true,
        },
    };
}

function _getStepFactoryUsageSuccess(state: StepFactoriesState, payload: ComponentInfo[]): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStepFactoryUsage: false,
        },
        stepFactoryUsage: payload,
    };
}



//*************************************************
//*****   GET Step Factory Version (Instance)   ***
//*************************************************
function _getStepFactoryVersionError(state: StepFactoriesState): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStepFactoryVersion: false,
        },
    };
}

function _getStepFactoryVersionRequest(state: StepFactoriesState): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStepFactoryVersion: true,
        },
    };
}

function _getStepFactoryVersionSuccess(state: StepFactoriesState, payload: StepFactoryVersionDto): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStepFactoryVersion: false,
        },
        stepFactoryVersionDetail: payload,
    };
}



//*************************************************
//***   GET Step Factory Version Autocomplete   ***
//*************************************************
function _getStepFactoryVersionAutocompleteError(state: StepFactoriesState, payload: ApiErrorJson | Record<string, never>): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStepFactoryVersionAutocomplete: false,
        },
        stepFactoryVersionAutocomplete: {
            ...state.stepFactoryVersionAutocomplete,
            error: payload
        },
    };
}

function _getStepFactoryVersionAutocompleteRequest(state: StepFactoriesState): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStepFactoryVersionAutocomplete: true,
        },
    };
}

function _getStepFactoryVersionAutocompleteSuccess(state: StepFactoriesState, payload: StepFactoryVersionAutocompleteSuggestions): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStepFactoryVersionAutocomplete: false,
        },
        stepFactoryVersionAutocomplete: {
            ...state.stepFactoryVersionAutocomplete,
            id: crypto?.randomUUID(),
            details: payload
        },
    };
}



//*************************************************
//*****   GET Step Factory Version Usage   ********
//*************************************************
function _getStepFactoryVersionsError(state: StepFactoriesState): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStepFactoryVersions: false,
        },
    };
}

function _getStepFactoryVersionsRequest(state: StepFactoriesState): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStepFactoryVersions: true,
        },
    };
}

function _getStepFactoryVersionsSuccess(state: StepFactoriesState, payload: StepFactoryVersionDto[]): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStepFactoryVersions: false,
        },
        stepFactoryVersionList: payload,
    };
}

function _getStepFactoryVersionUsageError(state: StepFactoriesState): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStepFactoryVersionUsage: false,
        },
    };
}

function _getStepFactoryVersionUsageRequest(state: StepFactoriesState): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStepFactoryVersionUsage: true,
        },
    };
}

function _getStepFactoryVersionUsageSuccess(state: StepFactoriesState, payload: ComponentInfo[]): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStepFactoryVersionUsage: false,
        },
        stepFactoryVersionUsage: payload,
    };
}



//*************************************************
//*****   GET Step Factories (Collection)   *******
//*************************************************
function getStepFactoriesError(state: StepFactoriesState): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStepFactories: false,
        },
    };
}

function getStepFactoriesRequest(state: StepFactoriesState): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStepFactories: true,
        },
    };
}

function getStepFactoriesSuccess(state: StepFactoriesState, payload: StepFactoryDto[]): StepFactoriesState {
    const [ ...stepFactoryList ] = payload;
    const listSortedByName = sortAz(stepFactoryList, 'name');
    const listSortedByType = sortAz(listSortedByName, 'type');

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStepFactories: false,
        },
        stepFactoryList: listSortedByType
    };
}



//*************************************************
//*****   UPDATE Step Factory (Instance)   ********
//*************************************************
function _updateStepFactoryError(state: StepFactoriesState): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateStepFactory: false,
        },
    };
}

function _updateStepFactoryRequest(state: StepFactoriesState): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateStepFactory: true,
        },
    };
}

function _updateStepFactorySuccess(state: StepFactoriesState, payload: StepFactoryDto): StepFactoriesState {
    const stepFactoriesList = [ ...state.stepFactoryList ];

    // if list exists, modify w updated step factory
    // note: list will not exist if deeplinked or refreshed detail page
    if (stepFactoriesList.length) {
        const updatedIndex = stepFactoriesList.findIndex(x => x.id === payload.id);
        if (updatedIndex > -1) stepFactoriesList[updatedIndex] = payload;
    }

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateStepFactory: false,
        },
        stepFactoryDetail: payload,
        stepFactoryList: stepFactoriesList,
    };
}



//*************************************************
//***   UPDATE Step Factory Version (Instance)   **
//*************************************************
function _updateStepFactoryVersionError(state: StepFactoriesState): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateStepFactoryVersion: false,
        },
    };
}

function _updateStepFactoryVersionRequest(state: StepFactoriesState): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateStepFactoryVersion: true,
        },
    };
}

function _updateStepFactoryVersionSuccess(state: StepFactoriesState, payload: StepFactoryVersionDto): StepFactoriesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateStepFactoryVersion: false,
        },
        stepFactoryVersionDetail: payload,
    };
}

export default stepFactoriesReducer as Reducer<StepFactoriesState>;
